// ============================================================
// AccessDetail — /olivium/secure-access/:accessId.
//
// Detail + lifecycle actions for one issued code (found in the real
// sq_access_codes list — there is no plaintext or hash here, only the public
// row). Revoke / Expire are real, audited mutations. The bound destination is
// shown honestly (a placeholder/unrouted target says "not bound to a
// destination" rather than a fabricated link).
// ============================================================

import { useState } from 'react'
import CommandCenterShell from '../../shell/CommandCenterShell'
import { CC_INK, CC_MONO } from '../../shell'
import { OLIVIUM_ACCENT } from '../portal-factory/portalFactory'
import { SectionCard, StateBlock, chipBtn, primaryBtn } from '../portal-factory/PortalKit'
import { ACCESS_STATUS_META } from './secureAccessData'
import type { CommandCenterAccessCode } from '../../../../api/client'

export interface AccessDetailProps {
  code: CommandCenterAccessCode | null
  loading: boolean
  error: string | null
  authRequired: boolean
  forbidden: boolean
  notFound: boolean
  mutationError?: string | null
  onHome?: () => void
  onBackCommandCenter?: () => void
  onOpenHub?: () => void
  onRevoke?: () => void
  onExpire?: () => void
  onOpenDestination?: (url: string) => void
}

function Row({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '5px 0', fontSize: 12 }}>
      <span style={{ color: CC_INK.faint, fontFamily: CC_MONO, width: 130, flexShrink: 0 }}>{label}</span>
      <span style={{ color: value ? CC_INK.secondary : CC_INK.faint, fontFamily: mono ? CC_MONO : 'inherit', wordBreak: 'break-all' }}>{value || '—'}</span>
    </div>
  )
}

export default function AccessDetail(props: AccessDetailProps) {
  const { code, loading, error, authRequired, forbidden, notFound, mutationError, onHome, onBackCommandCenter, onOpenHub, onRevoke, onExpire, onOpenDestination } = props
  const [confirmRevoke, setConfirmRevoke] = useState(false)

  if (!code) {
    return (
      <CommandCenterShell onHome={onHome} onBackCommandCenter={onBackCommandCenter} parentBack={{ label: '← Secure Access', onClick: onOpenHub }} breadcrumb={['OLIVIUM', 'SECURE ACCESS', 'CODE']} title={notFound ? 'Access code not found' : 'Access code'} titleAccent={OLIVIUM_ACCENT}>
        {loading && <StateBlock kind="loading" title="Loading…" />}
        {!loading && authRequired && <StateBlock kind="auth" title="Sign in to view this code" />}
        {!loading && forbidden && <StateBlock kind="error" title="Operator access required" />}
        {!loading && notFound && <StateBlock kind="empty" title="Access code not found" body="It may have been removed, or it belongs to another operator." />}
        {!loading && !authRequired && !forbidden && !notFound && error && <StateBlock kind="error" title="Could not load code" body={error} />}
        <div style={{ marginTop: 14 }}><button type="button" style={chipBtn} onClick={onOpenHub}>← Secure Access</button></div>
      </CommandCenterShell>
    )
  }

  const m = ACCESS_STATUS_META[code.status] || { label: code.status.toUpperCase(), accent: CC_INK.muted }
  const active = code.status === 'active'
  const dest = code.target_destination

  return (
    <CommandCenterShell
      onHome={onHome} onBackCommandCenter={onBackCommandCenter} parentBack={{ label: '← Secure Access', onClick: onOpenHub }}
      breadcrumb={['OLIVIUM', 'SECURE ACCESS', code.code_prefix]} title={code.label} titleAccent={OLIVIUM_ACCENT}
      statusBadge={<span style={{ marginLeft: 8, fontSize: 9, fontWeight: 800, fontFamily: CC_MONO, color: m.accent, padding: '3px 8px', borderRadius: 5, background: m.accent + '18', border: `1px solid ${m.accent}33` }}>{m.label}</span>}
    >
      <div>
        {mutationError && <div style={{ marginBottom: 14 }}><StateBlock kind="error" title="Last action failed" body={mutationError} /></div>}
        <SectionCard title="Code">
          <Row label="Prefix" value={code.code_prefix} mono />
          <Row label="Audience" value={code.audience} />
          <Row label="Role" value={code.role} />
          <Row label="Uses" value={`${code.use_count} / ${code.max_uses}`} mono />
          <Row label="Expires" value={code.expires_at ? code.expires_at.slice(0, 16).replace('T', ' ') : 'never'} mono />
          <Row label="Created" value={code.created_at.slice(0, 16).replace('T', ' ')} mono />
          {code.revoked_at && <Row label="Revoked" value={`${code.revoked_at.slice(0, 16).replace('T', ' ')} · ${code.revoke_reason || ''}`} />}
          <p style={{ margin: '8px 0 0', fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>Hash-at-rest — the plaintext was shown once at issue and is never retrievable here.</p>
        </SectionCard>

        <SectionCard title="Target binding">
          <Row label="Type" value={code.target_type ? code.target_type.replace(/_/g, ' ') : 'unbound'} />
          <Row label="Target id" value={code.target_id} mono />
          <Row label="Target label" value={code.target_label} />
          {dest ? (
            <div style={{ marginTop: 10 }}><button type="button" style={chipBtn} onClick={() => onOpenDestination?.(dest)}>Open destination</button></div>
          ) : (
            <p style={{ margin: '8px 0 0', fontSize: 11, color: CC_INK.faint }}>This code is not bound to a destination — nothing to open (honest; not fabricated).</p>
          )}
        </SectionCard>

        {active && (
          <SectionCard title="Lifecycle" hint="Real, audited actions on the sq_access_codes spine.">
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={chipBtn} onClick={onExpire}>Expire now</button>
              {confirmRevoke ? (
                <button type="button" style={{ ...primaryBtn, background: 'rgba(220,38,38,0.18)', borderColor: 'rgba(220,38,38,0.4)', color: '#f08a8a' }} onClick={onRevoke}>Confirm revoke</button>
              ) : (
                <button type="button" style={{ ...chipBtn, color: '#dc2626', borderColor: 'rgba(220,38,38,0.3)' }} onClick={() => setConfirmRevoke(true)}>Revoke</button>
              )}
            </div>
          </SectionCard>
        )}
      </div>
    </CommandCenterShell>
  )
}
